import { useEffect, useRef, useState } from "react";
import "./index.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChevronLeft,
  faChevronRight,
  faXmark,
} from "@fortawesome/free-solid-svg-icons";

const Slider = ({ images, name }) => {
  const [imageIndex, setImageIndex] = useState(null);
  const dialogRef = useRef(null);
  const openerRef = useRef(null);
  const isOpen = imageIndex !== null;

  const altFor = (index) =>
    `${name} screenshot ${index + 1} of ${images.length}`;

  const openAt = (index, e) => {
    openerRef.current = e.currentTarget;
    setImageIndex(index);
  };

  const close = () => setImageIndex(null);

  const changeSlide = (direction) => {
    if (direction === "left") {
      if (imageIndex === 0) {
        setImageIndex(images.length - 1);
      } else {
        setImageIndex(imageIndex - 1);
      }
    } else {
      if (imageIndex === images.length - 1) {
        setImageIndex(0);
      } else {
        setImageIndex(imageIndex + 1);
      }
    }
  };

  // Move focus into the viewer on open, and back to the thumbnail on close
  useEffect(() => {
    if (isOpen) {
      dialogRef.current?.querySelector(".close")?.focus();
    } else {
      openerRef.current?.focus();
    }
  }, [isOpen]);

  const onKeyDown = (e) => {
    if (e.key === "Escape") {
      close();
    } else if (e.key === "ArrowLeft") {
      changeSlide("left");
    } else if (e.key === "ArrowRight") {
      changeSlide("right");
    } else if (e.key === "Tab") {
      // Keep keyboard focus inside the viewer while it is open
      const buttons = [...dialogRef.current.querySelectorAll("button")];
      const first = buttons[0];
      const last = buttons[buttons.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  };

  return (
    <div className="slider">
      {isOpen && (
        <div
          className="fullSlider"
          role="dialog"
          aria-modal="true"
          aria-label={`${name} screenshots`}
          ref={dialogRef}
          onKeyDown={onKeyDown}
        >
          <button
            type="button"
            className="arrow"
            onClick={() => changeSlide("left")}
            aria-label="Previous screenshot"
          >
            <FontAwesomeIcon icon={faChevronLeft} />
          </button>
          <div className="imgContainer">
            <img src={images[imageIndex]} alt={altFor(imageIndex)} />
          </div>
          <button
            type="button"
            className="arrow"
            onClick={() => changeSlide("right")}
            aria-label="Next screenshot"
          >
            <FontAwesomeIcon icon={faChevronRight} />
          </button>
          <button
            type="button"
            className="close"
            onClick={close}
            aria-label="Close screenshots"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        </div>
      )}
      <div className="bigImage">
        <button
          type="button"
          onClick={(e) => openAt(0, e)}
          aria-label={`View ${altFor(0)} full screen`}
        >
          <img src={images[0]} alt={altFor(0)} />
        </button>
      </div>
      <div className="smallImages">
        {images.slice(1).map((image, index) => (
          <button
            type="button"
            key={index}
            onClick={(e) => openAt(index + 1, e)}
            aria-label={`View ${altFor(index + 1)} full screen`}
          >
            <img src={image} alt={altFor(index + 1)} />
          </button>
        ))}
      </div>
    </div>
  );
};

export default Slider;
